import React, { useState, useEffect } from 'react';
import API_URL from '../config';

function Overview({ token, onNavigate }) {
  const [data, setData] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const fetchData = async () => {
      try {
        const [overviewRes, logsRes] = await Promise.all([
          fetch(`${API_URL}/api/protected/overview`, { headers }),
          fetch(`${API_URL}/api/protected/logs?limit=8`, { headers }),
        ]);
        if (overviewRes.ok) setData(await overviewRes.json());
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setRecentLogs(logsData.logs || []);
        }
      } catch (e) {}
    };
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [token]);

  if (!data) return <div className="loading"><div className="spinner" /> Loading...</div>;

  const healthColor = data.health_score >= 80 ? 'var(--success)' :
                      data.health_score >= 50 ? 'var(--warning)' : 'var(--error)';

  const circumference = 2 * Math.PI * 48;
  const offset = circumference - (data.health_score / 100) * circumference;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-subtitle">System health and key metrics</div>
        </div>
      </div>
      <div className="page-body">
        <div className="metrics-grid">
          <div className="metric-card blue">
            <div className="metric-label">Health Score</div>
            <div className="health-ring">
              <svg viewBox="0 0 120 120">
                <circle className="health-ring-bg" cx="60" cy="60" r="48" />
                <circle className="health-ring-fill" cx="60" cy="60" r="48"
                  style={{ stroke: healthColor, strokeDasharray: circumference, strokeDashoffset: offset }} />
              </svg>
              <div className="health-ring-text" style={{ color: healthColor }}>{data.health_score}</div>
            </div>
          </div>
          <div className="metric-card green">
            <div className="metric-label">Active Agents</div>
            <div className="metric-value green">{data.agents_active}</div>
            <div className="metric-change">{data.agents_total} total registered</div>
          </div>
          <div className="metric-card purple">
            <div className="metric-label">Traces (24h)</div>
            <div className="metric-value purple">{data.traces_24h}</div>
            <div className="metric-change">{data.errors_24h} errors ({(data.error_rate * 100).toFixed(1)}%)</div>
          </div>
          <div className="metric-card yellow">
            <div className="metric-label">Events (24h)</div>
            <div className="metric-value yellow">{data.events_24h}</div>
            <div className="metric-change">Agent activity logged</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Cost & Tokens</div>
                <div className="card-subtitle">Last 24 hours</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
                <div className="text-xs text-muted" style={{ marginBottom: 4 }}>Total Cost</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)' }}>${data.cost_24h.toFixed(4)}</div>
              </div>
              <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
                <div className="text-xs text-muted" style={{ marginBottom: 4 }}>Total Tokens</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--purple)' }}>{data.tokens_24h.toLocaleString()}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
                <div className="text-xs text-muted" style={{ marginBottom: 4 }}>Policy Violations</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: data.violations_24h > 0 ? 'var(--error)' : 'var(--success)' }}>{data.violations_24h}</div>
              </div>
              <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)' }}>
                <div className="text-xs text-muted" style={{ marginBottom: 4 }}>Unacked Alerts</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: data.unacked_alerts > 0 ? 'var(--warning)' : 'var(--success)' }}>{data.unacked_alerts}</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Recent Activity</div>
              <button className="btn-ghost btn-sm" onClick={() => onNavigate('activity')}>View all</button>
            </div>
            {recentLogs.length === 0 ? (
              <div className="empty-state" style={{ padding: '30px 20px' }}>
                <div className="empty-state-icon">~</div>
                <p>No activity yet. Connect an agent to get started.</p>
                <button className="btn-primary btn-sm" onClick={() => onNavigate('agents')}>Register Agent</button>
              </div>
            ) : (
              <div>
                {recentLogs.map((log, i) => (
                  <div key={log.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < recentLogs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)' }} className="truncate">{log.action}</div>
                      <div className="text-xs text-muted">{log.agent_name}</div>
                    </div>
                    <span className={`badge ${log.status === 'success' ? 'badge-success' : 'badge-error'}`}>{log.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <div className="card-title">Quick Actions</div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={() => onNavigate('agents')}>Register Agent</button>
            <button className="btn-secondary" onClick={() => onNavigate('traces')}>View Traces</button>
            <button className="btn-secondary" onClick={() => onNavigate('governance')}>Set Up Policies</button>
            <button className="btn-secondary" onClick={() => onNavigate('simulations')}>Run Simulation</button>
            <button className="btn-secondary" onClick={() => onNavigate('vault')}>Manage Vault</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Overview;
